import React, { useState, useEffect } from 'react';
import { Thermometer, Droplets, Wind, Activity, AlertCircle, CheckCircle } from 'lucide-react';
import { Device } from '../../types';
import deviceService from '../../services/deviceService';
import {
  SensorPanelsContainer,
  LeftColumn,
  RightColumn,
  SensorPanel,
  PanelHeader,
  PanelIcon,
  PanelTitle,
  SensorList,
  SensorRow,
  SensorLabel,
  SensorValue
} from '../../styles/SensorPanelStyles';

interface NetatmoWeatherDetailsProps {
  device: Device;
  deviceId: string;
}

interface NetatmoData {
  temperature?: number;
  humidity?: number;
  co2?: number;
  noise?: number;
  pressure?: number;
  absolute_pressure?: number;
  min_temp?: number;
  max_temp?: number;
}

const NetatmoWeatherDetails: React.FC<NetatmoWeatherDetailsProps> = ({ device, deviceId }) => {
  const [latestData, setLatestData] = useState<NetatmoData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLatestData = async () => {
      try {
        const data = await deviceService.getLatestData(deviceId);
        if (data && data.decoded_json) {
          setLatestData(data.decoded_json);
        }
      } catch (error) {
        console.error('Error fetching latest data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchLatestData();
  }, [deviceId]);

  const getCO2Status = (co2: number | undefined): { status: 'normal' | 'warning' | 'critical', text: string, color: string } => {
    if (co2 === undefined) return { status: 'normal', text: 'No Data', color: '#6b7280' };
    if (co2 < 1000) return { status: 'normal', text: 'Good', color: '#10b981' };
    if (co2 < 1500) return { status: 'warning', text: 'Average', color: '#f59e0b' };
    if (co2 < 2000) return { status: 'warning', text: 'Poor', color: '#f97316' };
    return { status: 'critical', text: 'Unhealthy', color: '#ef4444' };
  };

  const getComfortLevel = (temp: number | undefined, humidity: number | undefined): string => {
    if (temp === undefined || humidity === undefined) return 'Unknown';

    const tempOk = temp >= 18 && temp <= 24;
    const humidityOk = humidity >= 30 && humidity <= 60;

    if (tempOk && humidityOk) return 'Comfortable';
    if (tempOk || humidityOk) return 'Moderate';
    return 'Uncomfortable';
  };

  const formatTemperature = (temp: number | undefined): string => {
    return temp !== undefined ? `${temp.toFixed(1)}°C` : 'N/A';
  };

  const formatHumidity = (humidity: number | undefined): string => {
    return humidity !== undefined ? `${humidity.toFixed(0)}%` : 'N/A';
  };

  const formatPressure = (pressure: number | undefined): string => {
    return pressure !== undefined ? `${pressure.toFixed(1)} mbar` : 'N/A';
  };

  const formatCO2 = (co2: number | undefined): string => {
    return co2 !== undefined ? `${co2.toFixed(0)} ppm` : 'N/A';
  };

  const formatNoise = (noise: number | undefined): string => {
    return noise !== undefined ? `${noise.toFixed(0)} dB` : 'N/A';
  };

  const co2Status = getCO2Status(latestData?.co2);
  const comfortLevel = getComfortLevel(latestData?.temperature, latestData?.humidity);

  if (loading) {
    return (
      <SensorPanelsContainer>
        <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
          Loading weather station data...
        </div>
      </SensorPanelsContainer>
    );
  }

  return (
    <SensorPanelsContainer>
      <LeftColumn>
        {/* Indoor Climate Panel */}
        <SensorPanel>
          <PanelHeader>
            <PanelIcon>
              <Thermometer size={16} />
            </PanelIcon>
            <PanelTitle>Indoor Climate</PanelTitle>
          </PanelHeader>
          <SensorList>
            <SensorRow>
              <SensorLabel>Temperature</SensorLabel>
              <SensorValue status="normal">{formatTemperature(latestData?.temperature)}</SensorValue>
            </SensorRow>
            <SensorRow>
              <SensorLabel>Humidity</SensorLabel>
              <SensorValue status="normal">{formatHumidity(latestData?.humidity)}</SensorValue>
            </SensorRow>
            <SensorRow>
              <SensorLabel>Min Temperature Today</SensorLabel>
              <SensorValue status={latestData?.min_temp !== undefined ? 'normal' : 'inactive'}>{formatTemperature(latestData?.min_temp)}</SensorValue>
            </SensorRow>
            <SensorRow>
              <SensorLabel>Max Temperature Today</SensorLabel>
              <SensorValue status={latestData?.max_temp !== undefined ? 'normal' : 'inactive'}>{formatTemperature(latestData?.max_temp)}</SensorValue>
            </SensorRow>
            <SensorRow>
              <SensorLabel>Comfort Level</SensorLabel>
              <SensorValue status="normal">{comfortLevel}</SensorValue>
            </SensorRow>
          </SensorList>
        </SensorPanel>

        {/* Air Quality Panel */}
        <SensorPanel>
          <PanelHeader>
            <PanelIcon>
              <Wind size={16} />
            </PanelIcon>
            <PanelTitle>Air Quality</PanelTitle>
          </PanelHeader>
          <SensorList>
            <SensorRow>
              <SensorLabel>CO₂ Level</SensorLabel>
              <SensorValue status={co2Status.status}>{formatCO2(latestData?.co2)}</SensorValue>
            </SensorRow>
            <SensorRow>
              <SensorLabel>Air Quality</SensorLabel>
              <SensorValue status="normal" style={{ color: co2Status.color }}>
                {co2Status.status === 'normal' ? <CheckCircle size={16} style={{ marginRight: '4px' }} /> : <AlertCircle size={16} style={{ marginRight: '4px' }} />}
                {co2Status.text}
              </SensorValue>
            </SensorRow>
            {latestData?.co2 && latestData.co2 > 1000 && (
              <SensorRow>
                <SensorLabel>Recommendation</SensorLabel>
                <SensorValue status="warning" style={{ fontSize: '12px' }}>
                  {latestData.co2 < 1500 ? 'Consider ventilation' : 'Ventilation recommended'}
                </SensorValue>
              </SensorRow>
            )}
          </SensorList>
        </SensorPanel>
      </LeftColumn>

      <RightColumn>
        {/* Atmospheric Pressure Panel */}
        <SensorPanel>
          <PanelHeader>
            <PanelIcon>
              <Activity size={16} />
            </PanelIcon>
            <PanelTitle>Atmospheric Pressure</PanelTitle>
          </PanelHeader>
          <SensorList>
            <SensorRow>
              <SensorLabel>Pressure</SensorLabel>
              <SensorValue status="normal">{formatPressure(latestData?.pressure)}</SensorValue>
            </SensorRow>
            <SensorRow>
              <SensorLabel>Absolute Pressure</SensorLabel>
              <SensorValue status={latestData?.absolute_pressure !== undefined ? 'normal' : 'inactive'}>{formatPressure(latestData?.absolute_pressure)}</SensorValue>
            </SensorRow>
            <SensorRow>
              <SensorLabel>Trend</SensorLabel>
              <SensorValue status="normal">
                {latestData?.pressure !== undefined && latestData.pressure > 1013 ? '↑ Rising' :
                 latestData?.pressure !== undefined && latestData.pressure < 1013 ? '↓ Falling' :
                 '→ Stable'}
              </SensorValue>
            </SensorRow>
          </SensorList>
        </SensorPanel>

        {/* Acoustic Environment Panel */}
        <SensorPanel>
          <PanelHeader>
            <PanelIcon>
              <Droplets size={16} />
            </PanelIcon>
            <PanelTitle>Acoustic Environment</PanelTitle>
          </PanelHeader>
          <SensorList>
            <SensorRow>
              <SensorLabel>Noise Level</SensorLabel>
              <SensorValue status={latestData?.noise !== undefined ? 'normal' : 'inactive'}>{formatNoise(latestData?.noise)}</SensorValue>
            </SensorRow>
            <SensorRow>
              <SensorLabel>Classification</SensorLabel>
              <SensorValue status="normal">
                {latestData?.noise === undefined ? 'No Data' :
                 latestData.noise < 50 ? 'Quiet' :
                 latestData.noise < 70 ? 'Moderate' :
                 'Noisy'}
              </SensorValue>
            </SensorRow>
          </SensorList>
        </SensorPanel>
      </RightColumn>
    </SensorPanelsContainer>
  );
};

export default NetatmoWeatherDetails;
